import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/auth.store';
import { config } from '../config';
const COMPLIANCE_API_URL = config.api.compliance;
// =============================================================================
// Auth Headers
// =============================================================================
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
async function handleResponse(response) {
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error(`HTTP 404: ${response.url}`);
        }
        const errorBody = await response.json().catch(() => ({ message: 'Unknown error' }));
        // API returns { error: { code, message, details } }
        const errorMessage = errorBody?.error?.message || errorBody?.message || `HTTP ${response.status}`;
        const errorDetails = errorBody?.error?.details?.errors;
        if (errorDetails && Array.isArray(errorDetails)) {
            // Include validation error details
            const detailStr = errorDetails.map((e) => e.path ? `${e.path}: ${e.message}` : e.message).join('; ');
            throw new Error(`${errorMessage}: ${detailStr}`);
        }
        throw new Error(errorMessage);
    }
    return response.json();
}
// =============================================================================
// Curriculum API Functions
// =============================================================================
export async function getCurriculums(params) {
    const searchParams = new URLSearchParams();
    if (params?.status)
        searchParams.set('status', params.status);
    if (params?.limit)
        searchParams.set('limit', String(params.limit));
    if (params?.cursor)
        searchParams.set('cursor', params.cursor);
    const url = searchParams.toString()
        ? `${COMPLIANCE_API_URL}/curriculums?${searchParams}`
        : `${COMPLIANCE_API_URL}/curriculums`;
    const response = await fetch(url, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
export async function getCurriculum(curriculumId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
export async function createCurriculum(data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
export async function updateCurriculum(curriculumId, data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
export async function archiveCurriculum(curriculumId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/archive`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
}
// =============================================================================
// Version API Functions
// =============================================================================
export async function getVersions(curriculumId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/versions`, {
        headers: getAuthHeaders(),
    });
    const data = await handleResponse(response);
    return data.versions;
}
export async function getVersion(curriculumId, versionId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/versions/${versionId}`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
export async function createVersion(curriculumId, data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/versions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
export async function updateVersion(curriculumId, versionId, data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/versions/${versionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
export async function publishVersion(curriculumId, versionId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/curriculums/${curriculumId}/versions/${versionId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ message: 'Unknown error' }));
        // API returns { error: { code, message } }
        const errorMessage = errorBody?.error?.message || errorBody?.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
    }
}
// =============================================================================
// Assignment API Functions
// =============================================================================
export async function getAssignments(params) {
    const searchParams = new URLSearchParams();
    if (params?.userId)
        searchParams.set('userId', params.userId);
    if (params?.curriculumId)
        searchParams.set('curriculumId', params.curriculumId);
    if (params?.status)
        searchParams.set('status', params.status);
    if (params?.limit)
        searchParams.set('limit', String(params.limit));
    if (params?.cursor)
        searchParams.set('cursor', params.cursor);
    const url = searchParams.toString()
        ? `${COMPLIANCE_API_URL}/assignments?${searchParams}`
        : `${COMPLIANCE_API_URL}/assignments`;
    const response = await fetch(url, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
export async function getAssignment(assignmentId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/${assignmentId}`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
export async function createAssignments(data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    return handleResponse(response);
}
export async function exemptAssignment(assignmentId, data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/${assignmentId}/exempt`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
}
export async function resetAssignment(assignmentId, data) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/${assignmentId}/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
}
export async function getMyAssignments(params) {
    const searchParams = new URLSearchParams();
    if (params?.status)
        searchParams.set('status', params.status);
    if (params?.limit)
        searchParams.set('limit', String(params.limit));
    if (params?.cursor)
        searchParams.set('cursor', params.cursor);
    const url = searchParams.toString()
        ? `${COMPLIANCE_API_URL}/my-assignments?${searchParams}`
        : `${COMPLIANCE_API_URL}/my-assignments`;
    const response = await fetch(url, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// Mark a content block as complete
export async function markBlockComplete(assignmentId, blockId, progressData) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/${assignmentId}/progress`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ blockId, progressData }),
    });
    return handleResponse(response);
}
// Start a new quiz attempt
export async function startQuizAttempt(assignmentId, blockId) {
    const response = await fetch(`${COMPLIANCE_API_URL}/assignments/${assignmentId}/quiz-attempts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ blockId }),
    });
    return handleResponse(response);
}
// Submit quiz answers
export async function submitQuizAttempt(attemptId, answers, assignmentId) {
    const url = assignmentId
        ? `${COMPLIANCE_API_URL}/quiz-attempts/${attemptId}/submit?assignmentId=${assignmentId}`
        : `${COMPLIANCE_API_URL}/quiz-attempts/${attemptId}/submit`;
    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ answers }),
    });
    return handleResponse(response);
}
// =============================================================================
// Dashboard API Functions
// =============================================================================
export async function getDashboardStats() {
    const response = await fetch(`${COMPLIANCE_API_URL}/dashboard/stats`, {
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// =============================================================================
// React Query Hooks
// =============================================================================
export function useCurriculums(params) {
    return useQuery({
        queryKey: ['curriculums', params],
        queryFn: () => getCurriculums(params),
        staleTime: 2 * 60 * 1000, // 2 minutes
    });
}
export function useCurriculum(curriculumId) {
    return useQuery({
        queryKey: ['curriculum', curriculumId],
        queryFn: () => getCurriculum(curriculumId),
        enabled: !!curriculumId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}
export function useVersions(curriculumId) {
    return useQuery({
        queryKey: ['curriculum-versions', curriculumId],
        queryFn: () => getVersions(curriculumId),
        enabled: !!curriculumId,
        staleTime: 2 * 60 * 1000,
    });
}
export function useVersion(curriculumId, versionId) {
    return useQuery({
        queryKey: ['curriculum-version', curriculumId, versionId],
        queryFn: () => getVersion(curriculumId, versionId),
        enabled: !!curriculumId && !!versionId,
        staleTime: 1 * 60 * 1000, // Reduced to 1 minute to avoid stale status issues
        refetchOnMount: 'always', // Always refetch when component mounts
    });
}
export function useAssignments(params) {
    return useQuery({
        queryKey: ['assignments', params],
        queryFn: () => getAssignments(params),
        staleTime: 2 * 60 * 1000,
    });
}
export function useAssignment(assignmentId) {
    return useQuery({
        queryKey: ['assignment', assignmentId],
        queryFn: () => getAssignment(assignmentId),
        enabled: !!assignmentId,
        staleTime: 5 * 60 * 1000,
    });
}
export function useMyAssignments(params) {
    return useQuery({
        queryKey: ['my-assignments', params],
        queryFn: () => getMyAssignments(params),
        staleTime: 2 * 60 * 1000,
    });
}
export function useDashboardStats() {
    return useQuery({
        queryKey: ['compliance-dashboard-stats'],
        queryFn: getDashboardStats,
        staleTime: 5 * 60 * 1000,
    });
}
// =============================================================================
// Mutation Hooks
// =============================================================================
export function useCreateCurriculum() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createCurriculum,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['curriculums'] });
        },
    });
}
export function useUpdateCurriculum() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ curriculumId, data }) => updateCurriculum(curriculumId, data),
        onSuccess: (_, { curriculumId }) => {
            void queryClient.invalidateQueries({ queryKey: ['curriculums'] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum', curriculumId] });
        },
    });
}
export function useArchiveCurriculum() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: archiveCurriculum,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['curriculums'] });
        },
    });
}
export function useCreateVersion() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ curriculumId, data, }) => createVersion(curriculumId, data),
        onSuccess: (_, { curriculumId }) => {
            void queryClient.invalidateQueries({ queryKey: ['curriculum-versions', curriculumId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum', curriculumId] });
        },
    });
}
export function useUpdateVersion() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ curriculumId, versionId, data, }) => updateVersion(curriculumId, versionId, data),
        onSuccess: (_, { curriculumId, versionId }) => {
            void queryClient.invalidateQueries({ queryKey: ['curriculum-versions', curriculumId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum', curriculumId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum-version', curriculumId, versionId] });
        },
    });
}
export function usePublishVersion() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ curriculumId, versionId }) => publishVersion(curriculumId, versionId),
        onSuccess: (_, { curriculumId, versionId }) => {
            // Invalidate all version-related queries to ensure fresh data
            void queryClient.invalidateQueries({ queryKey: ['curriculum-versions', curriculumId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum-version', curriculumId, versionId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculum', curriculumId] });
            void queryClient.invalidateQueries({ queryKey: ['curriculums'] });
        },
    });
}
export function useCreateAssignments() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createAssignments,
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['assignments'] });
            void queryClient.invalidateQueries({ queryKey: ['compliance-dashboard-stats'] });
        },
    });
}
export function useExemptAssignment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ assignmentId, reason }) => exemptAssignment(assignmentId, { reason }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['assignments'] });
            void queryClient.invalidateQueries({ queryKey: ['compliance-dashboard-stats'] });
        },
    });
}
export function useResetAssignment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ assignmentId, reason }) => resetAssignment(assignmentId, { reason }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['assignments'] });
            void queryClient.invalidateQueries({ queryKey: ['compliance-dashboard-stats'] });
        },
    });
}
export function useMarkBlockComplete() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ assignmentId, blockId, progressData, }) => markBlockComplete(assignmentId, blockId, progressData),
        onSuccess: (_, { assignmentId }) => {
            void queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] });
            void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
        },
    });
}
export function useStartQuizAttempt() {
    return useMutation({
        mutationFn: ({ assignmentId, blockId, }) => startQuizAttempt(assignmentId, blockId),
    });
}
export function useSubmitQuizAttempt() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attemptId, answers, assignmentId, }) => submitQuizAttempt(attemptId, answers, assignmentId),
        onSuccess: (_, { assignmentId }) => {
            if (assignmentId) {
                void queryClient.invalidateQueries({ queryKey: ['assignment', assignmentId] });
                void queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
            }
        },
    });
}
